import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { displayName } from '../utils/userLabel';
import { STORAGE_UNITS, describeLocation } from './specimens/StoragePanel';
import * as S from './specimens/styles';

/*
 * Where a banked slant is kept, and who put it there (migration 0050,
 * PUT /slants/{id}/storage): the unit, its Freezer ID, rack, box and well;
 * when the slant was preserved, by whom and as what organism; and the
 * Isolate ID -- the code of the specimen it descends from, which is what
 * goes on the tube.
 *
 * Every rule is the server's (routes/slants.py::update_slant_storage). The
 * form says them in advance and turns each refusal's code into a sentence.
 */

/** The five parts of a place, as the body names them. */
const LOCATION_FIELDS = ['storage_unit', 'storage_unit_label', 'storage_rack', 'storage_box', 'storage_well'];
/** The parts of a place inside a unit: nothing without one. */
const PLACE_PARTS = LOCATION_FIELDS.slice(1);
/** What a slant in a unit must also say (mushroom_bank_preservation_complete). */
const PRESERVATION_FIELDS = ['preserved_at', 'preserved_by', 'preserved_organism'];
const FIELDS = [...LOCATION_FIELDS, ...PRESERVATION_FIELDS];
/** Refusals the server names by code, each with its own sentence (storage.errors.*). */
const KNOWN_ERRORS = ['preservation_incomplete', 'specimen_code_required', 'well_occupied', 'invalid_storage',
  'invalid_text', 'invalid_preserved_at', 'unknown_technician'];

/** Today where the person is, as YYYY-MM-DD -- what a date input holds. */
export function localToday(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** A stored YYYY-MM-DD in the reader's own format. Read as a local date:
 *  new Date('2026-10-05') is midnight UTC, the evening before west of it. */
export function formatDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString() : '';
}

/** A refusal in the reader's language; the server's own words for one it has
 *  no code for. */
export function storageErrorMessage(data, t) {
  if (!KNOWN_ERRORS.includes(data.code)) return data.error || t('saveFailed');
  return t(`storage.errors.${data.code}`, {
    holder: data.holder,
    fields: (data.missing || []).map(f => t(`storage.missing.${f}`)).join(', '),
  });
}

const valueOf = (v) => (v === undefined || v === null || String(v).trim() === '' ? null : String(v).trim());

export default function SlantStoragePanel({ slant, canEdit, users = [], currentUser, onSaved }) {
  const { t } = useTranslation(['slants', 'common']);
  const { t: tSpec } = useTranslation('specimens');
  const [form, setForm] = useState(null);         // the edit form's values, or null while reading
  const [defaults, setDefaults] = useState({});   // preservation values the form filled in itself
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);       // { code, message } from a refusal

  const open = () => {
    // What a slant being banked most likely says: preserved today, by the
    // person at the bench, as its own taxon. Only where nothing is stored.
    const suggested = {
      preserved_at: localToday(),
      preserved_by: currentUser?.user_id || '',
      preserved_organism: slant.taxon_name || '',
    };
    const next = Object.fromEntries(FIELDS.map(f => [f, slant[f] ?? '']));
    const filled = {};
    PRESERVATION_FIELDS.forEach((f) => {
      if (!next[f] && suggested[f]) { next[f] = suggested[f]; filled[f] = suggested[f]; }
    });
    setForm(next);
    setDefaults(filled);
    setError(null);
  };

  const set = (key, tidy = (v) => v) => (e) => {
    const value = tidy(e.target.value);
    setForm(f => ({ ...f, [key]: value }));
  };

  // A well is a place only inside a unit (utils.storage_position_holder):
  // taking the slant out of storage empties the rest of its place, so no
  // freezer, rack or box is left claiming it.
  const setUnit = (e) => {
    const value = e.target.value;
    setForm(f => ({ ...f, storage_unit: value,
      ...(value ? {} : Object.fromEntries(PLACE_PARTS.map(p => [p, '']))) }));
  };

  const save = async (e) => {
    e.preventDefault();
    // Only what changed: a key sent is a key the server writes. Outside a
    // unit the rest of a place is nothing, and is cleared where it was set.
    const inUnitNow = !!valueOf(form.storage_unit);
    const body = {};
    FIELDS.forEach((f) => {
      const value = !inUnitNow && PLACE_PARTS.includes(f) ? null : valueOf(form[f]);
      if (value !== valueOf(slant[f])) body[f] = value;
    });
    // A suggestion nobody touched says nothing about a slant left out of
    // storage: it was not preserved today by whoever opened the form.
    if (!valueOf(form.storage_unit)) {
      Object.entries(defaults).forEach(([f, v]) => { if (form[f] === v) delete body[f]; });
    }
    if (Object.keys(body).length === 0) { setForm(null); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/slants/${slant.slant_id}/storage`, body);
      onSaved?.(res.data);
      setForm(null);
    } catch (err) {
      const data = err.response?.data || {};
      setError({ code: data.code, message: storageErrorMessage(data, t) });
    } finally {
      setSaving(false);
    }
  };

  // The technician list holds active accounts; one who has since left still
  // names the slant they banked, by the name the server gave ("Deleted user"
  // for a deleted account's anonymised one).
  const technicians = [...users];
  if (form?.preserved_by && !technicians.some(u => u.user_id === form.preserved_by)) {
    const name = form.preserved_by === slant.preserved_by ? displayName(slant.preserved_by_name, t)
      : form.preserved_by === currentUser?.user_id ? currentUser?.username : null;
    technicians.unshift({ user_id: form.preserved_by, username: name || form.preserved_by });
  }
  const inUnit = !!valueOf(form?.storage_unit);
  // The parts of a place, faded while there is no unit for them to be in.
  const placeInput = inUnit ? S.input : { ...S.input, opacity: 0.55 };
  // A slant out of storage is nowhere, whatever was once written beside it.
  const location = slant.storage_unit ? describeLocation(slant, tSpec) : '';

  return (
    <section aria-labelledby="slant-storage-title" data-testid="slant-storage" style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong id="slant-storage-title" style={{ fontSize: 13 }}>{t('storage.title')}</strong>
        {canEdit && !form && (
          <button type="button" onClick={open} style={S.ghostBtn} data-testid="slant-storage-edit"
                  title={t('tip.storageEdit')}>
            {t('storage.edit')}
          </button>
        )}
      </div>

      {!form && (
        <dl style={{ ...S.dl, marginTop: 8 }}>
          <dt style={S.dt}>{t('storage.isolateId')}</dt>
          <dd style={{ ...S.dd, ...S.mono }} dir="ltr" data-testid="slant-specimen-code">{slant.specimen_code || '—'}</dd>
          <dt style={S.dt}>{t('storage.location')}</dt>
          <dd style={S.dd} data-testid="slant-storage-location">{location || '—'}</dd>
          <dt style={S.dt}>{t('storage.preservedAt')}</dt>
          <dd style={S.dd}>{formatDay(slant.preserved_at) || '—'}</dd>
          <dt style={S.dt}>{t('storage.preservedBy')}</dt>
          <dd style={S.dd}>{displayName(slant.preserved_by_name, t) || '—'}</dd>
          <dt style={S.dt}>{t('storage.organism')}</dt>
          <dd style={S.dd}><span dir="ltr">{slant.preserved_organism || '—'}</span></dd>
        </dl>
      )}

      {form && (
        <form onSubmit={save} data-testid="slant-storage-form" style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('storage.isolateId')}: </span>
            <span dir="ltr" style={S.mono} data-testid="slant-storage-isolate">{slant.specimen_code || '—'}</span>
          </div>
          {!slant.specimen_code && (
            <p role="note" style={{ ...S.warning, margin: '0 0 10px' }} data-testid="slant-storage-no-isolate">
              {slant.storage_unit ? t('storage.noIsolateIdBanked') : t('storage.noIsolateId')}
            </p>
          )}
          <div style={gridStyle}>
            <label style={S.label}>
              {t('storage.unit')}
              <select value={form.storage_unit} onChange={setUnit} style={S.input}
                      title={t('tip.storageUnit')} data-testid="slant-storage-unit">
                <option value="">{t('storage.notStored')}</option>
                {STORAGE_UNITS.map(u => <option key={u} value={u}>{tSpec(`storage.units.${u}`)}</option>)}
              </select>
            </label>
            <label style={S.label}>
              {t('storage.freezerId')}
              <input value={form.storage_unit_label} onChange={set('storage_unit_label')} maxLength={60}
                     disabled={!inUnit} style={placeInput} placeholder={t('storage.freezerIdPlaceholder')} title={t('tip.freezerId')}
                     data-testid="slant-storage-label" />
            </label>
            <label style={S.label}>
              {t('storage.rack')}
              <input value={form.storage_rack} onChange={set('storage_rack')} maxLength={60} dir="ltr"
                     disabled={!inUnit} style={placeInput} title={t('tip.rack')} data-testid="slant-storage-rack" />
            </label>
            <label style={S.label}>
              {t('storage.box')}
              <input value={form.storage_box} onChange={set('storage_box')} maxLength={60} dir="ltr"
                     disabled={!inUnit} style={placeInput} title={t('tip.box')} data-testid="slant-storage-box" />
            </label>
            <label style={S.label}>
              {t('storage.well')}
              {/* "c 7" is C7 to the server too; written so here as it is typed. */}
              <input value={form.storage_well} onChange={set('storage_well', v => v.toUpperCase().replace(/\s+/g, ''))}
                     maxLength={5} dir="ltr" autoCapitalize="characters" autoComplete="off" spellCheck={false}
                     disabled={!inUnit} aria-invalid={error?.code === 'well_occupied' ? 'true' : undefined}
                     style={{ ...placeInput, ...S.mono, textTransform: 'uppercase' }} title={t('tip.well')}
                     data-testid="slant-storage-well" />
            </label>
          </div>
          <p style={{ ...S.hint, margin: '10px 0' }}>{t('storage.requiredWithUnit')}</p>
          <div style={gridStyle}>
            <label style={S.label}>
              {t('storage.preservedAt')}{inUnit ? ' *' : ''}
              <input type="date" value={form.preserved_at} onChange={set('preserved_at')} max={localToday()}
                     aria-required={inUnit ? 'true' : undefined} style={S.input} title={t('tip.preservedAt')}
                     data-testid="slant-storage-preserved-at" />
            </label>
            <label style={S.label}>
              {t('storage.preservedBy')}{inUnit ? ' *' : ''}
              <select value={form.preserved_by} onChange={set('preserved_by')} style={S.input}
                      aria-required={inUnit ? 'true' : undefined} title={t('tip.preservedBy')}
                      data-testid="slant-storage-preserved-by">
                <option value="">{t('selectUserPlaceholder')}</option>
                {technicians.map(u => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
              </select>
            </label>
            <label style={{ ...S.label, gridColumn: '1 / -1' }}>
              {t('storage.organism')}{inUnit ? ' *' : ''}
              <input value={form.preserved_organism} onChange={set('preserved_organism')} maxLength={300} dir="ltr"
                     aria-required={inUnit ? 'true' : undefined} style={S.input} title={t('tip.organism')}
                     data-testid="slant-storage-organism" />
            </label>
          </div>
          {error && <p role="alert" style={S.error} data-testid="slant-storage-error">{error.message}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button type="submit" disabled={saving} style={S.btn()} title={t('tip.storageSave')}
                    data-testid="slant-storage-save">
              {saving ? t('common:saving') : t('storage.save')}
            </button>
            <button type="button" onClick={() => { setForm(null); setError(null); }} style={S.ghostBtn}
                    title={t('tip.cancel')}>
              {t('common:cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

const sectionStyle = { marginTop: 16, borderTop: '1px solid var(--c-border)', paddingTop: 12 };

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: 10,
};
